/**
 * AddConceptModal — create a new Project Concept.
 *
 * Opens via CustomEvent('open-add-concept-modal', { detail: { projectId } }).
 *
 * On open, fetches the project's existing concepts to (a) show a live count and (b) prefill the
 * Parti Pris field with an auto-numbered default ("Concept N") the user is free to overwrite.
 *
 * On success, pushes the new record into ctx.conceptCache (if already loaded) and dispatches both
 * 'outline-render' (outline engine + most React sections) and 'concepts-updated' (DesignSection.jsx
 * listens for this specifically to reload its own Concepts section).
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { getConcepts, createConcept, setSelectedConcept, findConceptByName } from '../services/design.js'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const helpStyle = {
  fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)',
  lineHeight: 1.45, marginTop: 5,
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 13,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}

export default function AddConceptModal() {
  const [open, setOpen] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [count, setCount] = useState(null)     // existing concept count — null while resolving
  const [concepts, setConcepts] = useState([]) // the project's concepts — names must be unique per project
  const [name, setName] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [markSelected, setMarkSelected] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const pid = e.detail?.projectId || ''
      setProjectId(pid)
      setCount(null)
      setConcepts([])
      setName('')
      setNameTouched(false)
      setMarkSelected(false)
      setError(null)
      setSubmitting(false)
      setOpen(true)
      if (pid) {
        getConcepts(pid).then(records => { setConcepts(records || []); setCount(records.length) }).catch(() => setCount(0))
      } else {
        setCount(0)
      }
    }
    window.addEventListener('open-add-concept-modal', onOpen)
    return () => window.removeEventListener('open-add-concept-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Auto-numbered default ("Concept N") once the count resolves — only while the user hasn't
  // typed anything yet, so a slow fetch never clobbers what they've already entered.
  // Skips past any "Concept N" already taken, so the default is always a free name.
  useEffect(() => {
    if (count == null || nameTouched) return
    let n = count + 1
    while (findConceptByName(concepts, 'Concept ' + n)) n++
    setName('Concept ' + n)
  }, [count, concepts, nameTouched])

  const dupe = findConceptByName(concepts, name)

  async function handleSubmit() {
    if (!projectId || !name.trim() || submitting || dupe) return
    setSubmitting(true); setError(null)
    try {
      const created = await createConcept(projectId, { 'Parti Pris Title': name.trim() })
      if (markSelected) await setSelectedConcept(projectId, created.id)
      if (Array.isArray(ctx.conceptCache)) {
        created.fields = { ...created.fields, 'Selected Concept': markSelected }
        if (markSelected) ctx.conceptCache.forEach(c => { if (c.fields) c.fields['Selected Concept'] = false })
        ctx.conceptCache = [...ctx.conceptCache, created]
      }
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
      window.dispatchEvent(new CustomEvent('concepts-updated', { detail: { projectId } }))
    } catch (ex) {
      setError(ex.message || 'Failed to create concept'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = !!name.trim() && !submitting && !dupe

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddConceptModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            Add Concept{count != null && <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}> · #{count + 1}</span>}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
          <div>
            <label style={labelStyle}>Parti Pris</label>
            <input
              style={inputStyle} value={name} autoFocus
              onChange={e => { setNameTouched(true); setName(e.target.value) }}
              placeholder={count != null ? 'Concept ' + (count + 1) : 'Concept name'}
            />
            {dupe && (
              <div style={{ ...helpStyle, color: '#c0392b' }}>
                Another concept is already named "{dupe.fields['Parti Pris Title']}". Concept names must be unique within a project.
              </div>
            )}
            <div style={helpStyle}>
              The concept's core idea in a few words — the organizing move that defines this scheme
              (e.g. "Threshold and Reveal", "Split Along the Ravine"), not a nickname. Leave the
              auto-numbered default if you'd rather name it once the design settles.
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={markSelected} onChange={e => setMarkSelected(e.target.checked)}
              style={{ marginTop: 2, accentColor: '#1D9E75', cursor: 'pointer' }} />
            <div>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>Set as the selected concept</div>
              <div style={helpStyle}>Drives presentations, specs, and schedules for this project. Clears the flag on any other concept.</div>
            </div>
          </label>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : 'Add Concept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
